import { useContext, useEffect, useRef, useState } from "react";
import "../../../../styles/global.css";
import { SignalRContext } from "../../../../contexts/SignalRContext";
import CriticalEventsCard from "./CriticalEventsCard";
import dayjs from "dayjs";
import type { EventDataintr } from "@/shared/types/common.types";

interface CriticalEventsPanelProps {
  activeBtn?: string | null;
  onSelect: (value: string) => void;
}

function CriticalEventsPanel({
  activeBtn,
  onSelect,
}: CriticalEventsPanelProps) {
  const { connection } = useContext(SignalRContext);
  const [isOpen, setIsOpen] = useState(false);
  const [eventsData, setEventsData] = useState<EventDataintr[]>([]);
  useEffect(() => {
    setIsOpen(activeBtn === "bolt");
  }, [activeBtn]);
  const sidebarRef = useRef<HTMLDivElement>(null);

  function getDurationFromNow(updatetime: string): string {
    const now = dayjs();
    const updateTime = dayjs(updatetime);

    const diffInMinutes = now.diff(updateTime, "minute");
    const diffInHours = now.diff(updateTime, "hour");
    const diffInDays = now.diff(updateTime, "day");

    return diffInMinutes < 1
      ? "Just now"
      : diffInMinutes < 60
        ? `${diffInMinutes} min ago`
        : diffInHours < 24
          ? `${diffInHours} hr ago`
          : `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  }

  const [eventCount, setEventCount] = useState(0);

  useEffect(() => {
    if (!connection) return;

    const handleEvents = (Events: any) => {
      const eventsData: EventDataintr[] = Events.map((event: any) => ({
        Customer: event.customer,
        Product: event.product,
        Usecase: event.usecase,
        namespacename: event.namespacename,
        subscriptionid: event.subscriptionid,
        updatetime: event.updatetime,
      }));
      setEventCount(eventsData.length);
      setEventsData(eventsData);
    };

    connection.on("CriticalEvents", handleEvents);
    return () => connection.off("CriticalEvents", handleEvents);
  }, [connection]);

  return (
    <div>
      <button
        type="button"
        className="icon-button"
        onClick={() => onSelect("bolt")}
        aria-label="Open Critical Events"
      >
        <div className="tooltip-wrapper">
          <i className="fa-solid fa-bolt fa-lg text-[var(--text-primary)]"></i>
          <span className="tooltip-text">Critical Events</span>
        </div>
        {eventCount > 0 && (
          <span
            className="icon-button__badge"
            style={{
              backgroundColor: "var(--severity-critical)",
              border: "1px solid var(--severity-critical)",
              color: "var(--btn-danger-text)",
              minWidth: "20px",
              height: "20px",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              fontSize: "0.75rem"
            }}
          >
            {eventCount}
          </span>
        )}
      </button>

      <div
        ref={sidebarRef}
        className={`critical-events-sidebar fixed top-[85px] right-0 h-[calc(100vh-64px)] bg-[var(--bg-surface)] border-l border-[var(--border-default)] shadow-md transform transition-transform duration-300 ease-in-out z-[99999] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ width: '340px' }}
      >
        <div className="panel-header">
          <span>Critical Events</span>
          <button
            onClick={() => onSelect("bolt")}
            className="text-xl cursor-pointer"
            aria-label="Close Critical Events"
            style={{ color: 'var(--text-primary)', background: 'transparent', border: 'none' }}
          >
            &times;
          </button>
        </div>

        <div className="panel-body">
          <div
                      className={`critical-events-scroll space-y-3 pr-1 ${eventsData.length > 0 ? "overflow-y-auto" : ""}`}
            style={{ height: '84vh' }}
          >
            {eventsData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-[var(--text-tertiary)] text-sm mt-10">
                <i className="pi pi-bell-off text-3xl mb-3 text-[var(--text-disabled)]" />
                <p>No new Critical Events</p>
              </div>
            ) : (
              eventsData.map((item, idx) => (
                <CriticalEventsCard
                  key={idx}
                  details={
                    item.Customer?.toUpperCase() +
                    "/" +
                    item.Usecase?.toUpperCase() +
                    "/" +
                    item.Product?.toUpperCase()
                  }
                  timeAgo={getDurationFromNow(item.updatetime)}
                  lastStatus={item.updatetime}
                  title="MDC Pod is down"
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CriticalEventsPanel;
